import { useMe, useSubscription } from "@profitlens/api-client";
import type { Plan, TenantOut, UserOut } from "@profitlens/api-client";

import { useAuthStore } from "@/stores/auth-store";

export interface Session {
  status: "unknown" | "authenticating" | "authenticated" | "anonymous";
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserOut | null;
  tenant: TenantOut | null;
}

/**
 * The single source of truth for "who am I" is the react-query cache (`useMe`);
 * the store only tracks the token and the coarse status.
 */
export function useSession(): Session {
  const status = useAuthStore((state) => state.status);
  const isAuthenticated = status === "authenticated";
  const me = useMe({ enabled: isAuthenticated });

  return {
    status,
    isAuthenticated,
    isLoading:
      status === "unknown" || status === "authenticating" || (isAuthenticated && me.isLoading),
    user: me.data?.user ?? null,
    tenant: me.data?.tenant ?? null,
  };
}

export interface Entitlements {
  /** `effective_plan`, never `plan`: a free tenant is still Pro during the trial. */
  effectivePlan: Plan;
  isPro: boolean;
  isTrialing: boolean;
  trialDaysLeft: number;
  isOwner: boolean;
  statusLabel: string;
}

export function useEntitlements(): Entitlements {
  const { tenant, user } = useSession();
  const subscription = useSubscription({ enabled: Boolean(tenant) });

  const effectivePlan: Plan = tenant?.effective_plan ?? "free";
  const isTrialing = subscription.data?.is_trialing ?? tenant?.is_trialing ?? false;

  return {
    effectivePlan,
    isPro: effectivePlan === "pro",
    isTrialing,
    trialDaysLeft: subscription.data?.trial_days_left ?? tenant?.trial_days_left ?? 0,
    isOwner: user?.role === "owner" || user?.role === "platform_admin",
    statusLabel: subscription.data?.status ?? "none",
  };
}
